<script setup lang="ts">
/**
 * Moyens de paiement LOCAUX d'une page de don : Bancontact, Wero, iDEAL |
 * Wero (associations néerlandaises), prélèvement SEPA, Revolut Pay — ceux que
 * l'association a allumés.
 *
 * Placé EN HAUT, sous Apple Pay / Google Pay (Karim 01/11/2026 : « c'est pas
 * plus logique de l'avoir vers le bouton Apple Pay ? ») : c'est un paiement
 * express comme eux. Mais la banque ne fournit ni l'e-mail ni le nom : un
 * petit panneau s'ouvre JUSTE DESSOUS avec seulement ce qui manque (e-mail,
 * nom du titulaire) et « Continuer vers … ». Les champs du panneau sont ceux
 * du formulaire (mêmes valeurs, rien à retaper plus bas).
 *
 * Un seul moyen : son bouton « Payer avec … », qui part tout de suite quand
 * rien ne manque. Deux ou plus : regroupés derrière « Autres moyens de
 * paiement » ; une fois dépliés, des TUILES (logo + nom), deux par ligne,
 * plutôt qu'une longue colonne de boutons (Karim 02/11/2026 : « ya bcp de
 * boutons quand il active tout »). Une tuile CHOISIT le moyen : elle ouvre
 * toujours le panneau, et c'est « Continuer vers … » qui part vers la banque
 * — un bouton dit exactement ce qu'il fait.
 *
 * Affiché seulement quand l'API en publie (`payment_providers.stripe.
 * moyens_locaux`). Jamais dans le widget intégré ; pour un don mensuel,
 * seulement le prélèvement SEPA — la page parente en décide.
 *
 * Prélèvement SEPA (02/11/2026) : pas de redirection — le panneau s'ouvre
 * toujours, avec le formulaire de Stripe (IBAN + texte du mandat SEPA) monté
 * par la page (`prelevement`) ; le don est confirmé quelques jours plus tard.
 * Le nom du titulaire est le « Nom complet » du formulaire quand la page
 * l'affiche (Karim 21/10 : pas de second nom) ; à part seulement pour un don
 * anonyme ou une page sans champ « nom ».
 */
import { nextTick, onBeforeUnmount, useId } from "vue";
import { MOYENS_PAIEMENT_LOCAUX } from "@donoor/utils";
import { isValidEmailStrict } from "@donoor/validators";
import type { MoyenPaiementLocalPage } from "@donoor/checkout";
import BoiteHauteurAnimee from "~/components/shared/BoiteHauteurAnimee.vue";
import { cleTexteTitulaire } from "~/utils/texteTitulaireMoyenLocal";

const props = withDefaults(
  defineProps<{
    moyens: MoyenPaiementLocalPage[];
    /** Nom saisi dans le formulaire (vide si anonyme ou champ absent). */
    nomDonateur: string;
    /** Le formulaire affiche « Nom complet » : le nom du titulaire vient de lui. */
    nomDemandeEnHaut?: boolean;
    /** Don anonyme : le nom du titulaire va à la banque, jamais à l'association. */
    anonyme?: boolean;
    /** E-mail saisi dans le formulaire (le panneau le complète au besoin). */
    email?: string;
    /** La page exige l'e-mail du donateur. */
    emailRequis?: boolean;
    /** Montant du don, déjà mis en forme (« 25,00 € ») ; null : pas encore choisi. */
    montant?: string | null;
    isLoading: boolean;
    isPreviewMode?: boolean;
    /** Refus du paiement (moyen indisponible, banque injoignable…), déjà traduit. */
    erreur?: string | null;
    /** La banque refuse le nom du titulaire : message affiché SUR ce champ. */
    erreurNom?: string | null;
    /** La banque refuse l'e-mail : message affiché SUR ce champ. */
    erreurEmail?: string | null;
    /**
     * Prélèvement SEPA (02/11/2026) : monte / démonte le formulaire de Stripe
     * (IBAN + texte du mandat) dans le panneau du moyen.
     */
    prelevement?: { monter: (conteneur: string) => Promise<boolean>; demonter: () => void } | null;
    /** Don mensuel choisi (textes du prélèvement). */
    mensuel?: boolean;
  }>(),
  {
    nomDemandeEnHaut: false,
    anonyme: false,
    email: "",
    emailRequis: true,
    montant: null,
    isPreviewMode: false,
    erreur: null,
    erreurNom: null,
    erreurEmail: null,
    prelevement: null,
    mensuel: false,
  },
);

const emit = defineEmits<{
  payer: [choix: { moyen: MoyenPaiementLocalPage; nomTitulaire: string }];
  "update:email": [valeur: string];
  "update:nomDonateur": [valeur: string];
}>();

const { t } = useI18n();

/** Nom du titulaire quand le formulaire ne le demande pas (anonyme, champ absent). */
const nomTitulaire = ref("");
/** Moyen dont le panneau est ouvert (un seul à la fois). */
const panneau = ref<MoyenPaiementLocalPage | null>(null);
/**
 * Moyen dont le panneau est À L'ÉCRAN : il y reste le temps que le panneau se
 * referme en douceur, puis s'efface (`panneauReferme`).
 */
const panneauAffiche = ref<MoyenPaiementLocalPage | null>(null);
/**
 * Champs montrés dans le panneau, figés à son ouverture : un champ ne doit
 * pas disparaître pendant qu'on le remplit.
 */
const champs = reactive({ email: false, nom: false });
const tentee = ref(false);

/** Nom de la marque ; le prélèvement SEPA, lui, se traduit. */
const nomDuMoyen = (moyen: MoyenPaiementLocalPage) =>
  moyen === "sepa_debit" ? t("don.moyensLocaux.nomSepa") : MOYENS_PAIEMENT_LOCAUX[moyen].nom;
/** Prélèvement SEPA : IBAN + mandat dans le panneau, jamais de redirection. */
const estPrelevement = (moyen: MoyenPaiementLocalPage) => moyen === "sepa_debit";
/**
 * Logo officiel de chaque moyen (badges 40×26, mêmes fichiers que les
 * réglages du tableau de bord et de l'appli — Karim 02/11/2026 : « mets les
 * bons logos de chaque moyen ») et couleur de la marque pour le texte.
 */
const STYLE_MOYEN: Record<MoyenPaiementLocalPage, { logo: string; texte: string }> = {
  bancontact: { logo: "/icons/moyens/bancontact.svg", texte: "#005498" },
  ideal: { logo: "/icons/moyens/ideal.svg", texte: "#CC0066" },
  // Wero : texte noir (du texte jaune sur fond blanc serait illisible).
  wero: { logo: "/icons/moyens/wero.svg", texte: "#111111" },
  revolut_pay: { logo: "/icons/moyens/revolut-pay.svg", texte: "#191C1F" },
  sepa_debit: { logo: "/icons/moyens/sepa.svg", texte: "#1F2A44" },
};

/** iDEAL et Bancontact refusent un nom de titulaire de moins de 3 caractères (Stripe). */
const NOM_TITULAIRE_MIN = 3;

const nomActuel = computed(() => (props.nomDemandeEnHaut ? props.nomDonateur : nomTitulaire.value).trim());
// Même règle que le paiement (Stripe) : une adresse qu'il accepterait.
const emailOk = computed(() => !props.emailRequis || isValidEmailStrict(props.email));
const nomOk = computed(() => nomActuel.value.length >= NOM_TITULAIRE_MIN);

/** Moyen choisi en dernier : un refus de la banque rouvre SON panneau. */
const dernierMoyen = ref<MoyenPaiementLocalPage | null>(null);
/** Refus de la banque sur un champ, effacé dès que le donateur corrige. */
const refusNom = ref<string | null>(null);
const refusEmail = ref<string | null>(null);

function ouvrirSurRefus(champ: "nom" | "email", message: string | null) {
  if (!message || !dernierMoyen.value) return;
  if (champ === "nom") refusNom.value = message;
  else refusEmail.value = message;
  champs[champ] = true;
  panneau.value = dernierMoyen.value;
}
watch(() => props.erreurNom, (m) => ouvrirSurRefus("nom", m ?? null));
watch(() => props.erreurEmail, (m) => ouvrirSurRefus("email", m ?? null));

/** Message du champ « nom » : refus de la banque, sinon contrôle de la page. */
const messageNom = (moyen: MoyenPaiementLocalPage) =>
  refusNom.value ?? (tentee.value && !nomOk.value ? t(cleTexteTitulaire("nomRequis", moyen), { moyen: nomDuMoyen(moyen) }) : "");
const messageEmail = computed(() => refusEmail.value ?? (tentee.value && !emailOk.value ? t("don.moyensLocaux.emailInvalide") : ""));

/** Valeur du champ « nom » du panneau : celui du formulaire, ou le nom du titulaire à part. */
const nomPanneau = computed({
  get: () => (props.nomDemandeEnHaut ? props.nomDonateur : nomTitulaire.value),
  set: (v: string) => {
    refusNom.value = null;
    if (props.nomDemandeEnHaut) emit("update:nomDonateur", v);
    else nomTitulaire.value = v;
  },
});
const emailPanneau = computed({
  get: () => props.email,
  set: (v: string) => {
    refusEmail.value = null;
    emit("update:email", v);
  },
});

/** Ouvre le panneau de ce moyen, avec seulement ce qui manque. */
function ouvrirPanneau(moyen: MoyenPaiementLocalPage) {
  dernierMoyen.value = moyen;
  if (panneau.value !== moyen) {
    champs.email = !emailOk.value;
    champs.nom = !nomOk.value;
    tentee.value = false;
  } else {
    tentee.value = true;
  }
  panneau.value = moyen;
}

/**
 * « Payer avec … » (un seul moyen) et « Continuer vers … » (panneau) : part
 * vers la banque si rien ne manque, sinon ouvre ou complète le panneau.
 */
function payer(moyen: MoyenPaiementLocalPage) {
  if (props.isLoading || props.isPreviewMode) return;
  dernierMoyen.value = moyen;
  // Prélèvement : l'IBAN se saisit dans le panneau, qui s'ouvre toujours.
  const panneauRequis = estPrelevement(moyen) && panneau.value !== moyen;
  if (panneauRequis || !props.montant || !emailOk.value || !nomOk.value) {
    ouvrirPanneau(moyen);
    return;
  }
  emit("payer", { moyen, nomTitulaire: nomActuel.value });
}

/**
 * Tuile (plusieurs moyens) : choisit le moyen et ouvre son panneau, sans
 * jamais partir vers la banque ; un second clic sur la tuile choisie le
 * referme.
 */
function choisir(moyen: MoyenPaiementLocalPage) {
  if (props.isLoading || props.isPreviewMode) return;
  if (panneau.value === moyen) {
    fermer();
    return;
  }
  refusNom.value = null;
  refusEmail.value = null;
  ouvrirPanneau(moyen);
}

/**
 * Le donateur préfère finalement un autre moyen (carte, Apple Pay…) : il
 * referme le panneau. Ce qu'il a tapé reste dans le formulaire de la page.
 */
function fermer() {
  panneau.value = null;
  tentee.value = false;
  refusNom.value = null;
  refusEmail.value = null;
}

/** Panneau refermé à l'écran : son contenu s'efface (sauf s'il s'est rouvert entre-temps). */
function panneauReferme() {
  if (!panneau.value) panneauAffiche.value = null;
}
watch(panneau, (moyen) => {
  if (moyen) panneauAffiche.value = moyen;
});

/**
 * Deux moyens locaux ou plus : regroupés derrière « Autres moyens de
 * paiement », qui se déplie et se replie en douceur (Karim 02/11/2026 :
 * « sinon on risque d'avoir trop de lignes »). Apple Pay, Google Pay et
 * PayPal restent au-dessus, hors de ce compte. Un seul moyen : son bouton
 * directement, comme avant.
 */
const regroupes = computed(() => props.moyens.length >= 2);
const deplie = ref(false);
const idListe = useId();
const idPanneau = useId();

/** Replier la liste referme aussi le panneau du moyen choisi. */
function basculerListe() {
  if (deplie.value) fermer();
  deplie.value = !deplie.value;
}
// Un panneau qui s'ouvre (refus de la banque) ou un refus à montrer : la liste se déplie.
watch(panneau, (ouvert) => {
  if (ouvert) deplie.value = true;
});
watch(
  () => props.erreur,
  (erreur) => {
    if (erreur) deplie.value = true;
  },
  { immediate: true },
);
// L'association éteint EN DIRECT le moyen choisi : son panneau se referme.
watch(
  () => props.moyens,
  (moyens) => {
    if (panneau.value && !moyens.includes(panneau.value)) fermer();
  },
);

/**
 * Formulaire de prélèvement de Stripe, monté quand son panneau apparaît et
 * démonté quand il a fini de se refermer (ou quand la page part) : il reste
 * visible pendant que le panneau se referme.
 */
const idSepa = `sepa-${useId().replace(/[^a-zA-Z0-9_-]/g, "")}`;
const formulaireSepaIndisponible = ref(false);
watch(panneauAffiche, async (affiche, avant) => {
  if (avant === "sepa_debit") props.prelevement?.demonter();
  if (affiche === "sepa_debit") {
    formulaireSepaIndisponible.value = false;
    await nextTick();
    const monte = (await props.prelevement?.monter(idSepa)) ?? false;
    formulaireSepaIndisponible.value = !monte;
  }
});
onBeforeUnmount(() => {
  if (panneauAffiche.value === "sepa_debit") props.prelevement?.demonter();
});

/** « Changer le montant » : remonte au choix du montant de la page. */
function changerMontant() {
  if (typeof document === "undefined") return;
  document.querySelector("[data-montant-don]")?.scrollIntoView({ behavior: "smooth", block: "center" });
}
</script>

<template>
  <div v-if="moyens.length > 0" class="mb-3" data-testid="moyens-locaux">
    <!-- Un seul moyen : son bouton directement. -->
    <template v-if="!regroupes">
      <button
        v-for="moyen in moyens"
        :key="moyen"
        type="button"
        :data-testid="`payer-${moyen}`"
        :disabled="isLoading"
        :aria-disabled="isPreviewMode || undefined"
        class="w-full h-[44px] rounded-md border border-gray-300 bg-white flex items-center justify-center gap-2 text-[15px] font-semibold transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
        :class="isPreviewMode ? 'cursor-default' : 'hover:bg-gray-50'"
        :style="{ color: STYLE_MOYEN[moyen].texte }"
        @click="payer(moyen)"
      >
        <img :src="STYLE_MOYEN[moyen].logo" alt="" aria-hidden="true" width="40" height="26" class="h-[26px] w-10 shrink-0">
        {{ estPrelevement(moyen) ? t("don.moyensLocaux.payerParPrelevement") : t("don.moyensLocaux.payerAvec", { moyen: nomDuMoyen(moyen) }) }}
      </button>
    </template>

    <!-- Plusieurs moyens : « Autres moyens de paiement », puis des tuiles. -->
    <template v-else>
      <button
        type="button"
        data-testid="autres-moyens"
        :aria-expanded="deplie"
        :aria-controls="idListe"
        class="w-full min-h-[44px] py-2 rounded-md border border-gray-300 bg-white flex flex-wrap items-center justify-center gap-x-2 gap-y-1.5 text-[15px] font-semibold text-gray-900 transition-colors hover:bg-gray-50"
        @click="basculerListe"
      >
        <!-- Écran étroit (4 logos + libellé ne tiennent pas sur une ligne) :
             les logos passent au-dessus du libellé, jamais de texte coupé. -->
        <span aria-hidden="true" class="flex items-center gap-1">
          <img v-for="moyen in moyens" :key="moyen" :src="STYLE_MOYEN[moyen].logo" alt="" width="40" height="26" class="h-[22px] w-auto shrink-0">
        </span>
        <span class="inline-flex items-center gap-2 whitespace-nowrap">
          {{ t("don.moyensLocaux.autresMoyens") }}
          <UIcon
            name="i-lucide-chevron-down"
            aria-hidden="true"
            class="size-4 text-gray-500 transition-transform duration-300 motion-reduce:transition-none"
            :class="deplie ? 'rotate-180' : ''"
          />
        </span>
      </button>

      <!-- Tuiles : dépliées en douceur (grille 0fr → 1fr, sans mesurer le
           contenu) avec un fondu ; fondu seul si l'appareil demande moins
           d'animations. Jamais de `grid-rows-[1fr]` fixe sur l'élément : il
           passe après `grid-rows-[0fr]` dans la feuille de style et l'emporte,
           la hauteur saute alors d'un coup (Karim 02/11/2026 : « pas très
           smooth »). -->
      <Transition
        enter-active-class="transition-[grid-template-rows,opacity] duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-opacity"
        enter-from-class="grid-rows-[0fr] opacity-0"
        enter-to-class="grid-rows-[1fr] opacity-100"
        leave-active-class="transition-[grid-template-rows,opacity] duration-200 ease-in motion-reduce:transition-opacity"
        leave-from-class="grid-rows-[1fr] opacity-100"
        leave-to-class="grid-rows-[0fr] opacity-0"
      >
        <div v-if="deplie" :id="idListe" class="grid" data-testid="liste-moyens-locaux">
          <div class="min-h-0 overflow-hidden">
            <!-- Deux par ligne, compactes (Karim 02/11/2026 : « bcp de vide
                 blanc ») : logo à gauche du nom, même hauteur que les autres
                 boutons ; un nom long passe sur deux lignes. Nombre impair :
                 la dernière prend toute la largeur. Anneaux « inset » : la
                 boîte qui se déplie couperait un anneau extérieur. -->
            <div class="pt-2 grid grid-cols-2 gap-2">
              <button
                v-for="moyen in moyens"
                :key="moyen"
                type="button"
                :data-testid="`choisir-${moyen}`"
                :aria-pressed="panneau === moyen"
                :aria-controls="idPanneau"
                :disabled="isLoading"
                :aria-disabled="isPreviewMode || undefined"
                class="min-h-[44px] rounded-md border bg-white px-3 py-1.5 flex items-center justify-center gap-2 odd:last:col-span-2 transition-[border-color,box-shadow,background-color] duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-gray-900 disabled:opacity-60 disabled:cursor-not-allowed"
                :class="[
                  panneau === moyen ? 'border-gray-900 ring-1 ring-inset ring-gray-900' : 'border-gray-300',
                  isPreviewMode ? 'cursor-default' : 'hover:bg-gray-50',
                ]"
                @click="choisir(moyen)"
              >
                <img :src="STYLE_MOYEN[moyen].logo" alt="" aria-hidden="true" width="40" height="26" class="h-[22px] w-auto shrink-0">
                <span class="text-sm font-semibold leading-tight text-left text-gray-900">{{ nomDuMoyen(moyen) }}</span>
              </button>
            </div>
          </div>
        </div>
      </Transition>
    </template>

    <!-- Panneau du moyen choisi, juste dessous : ce qui manque pour partir
         vers la banque (ou l'IBAN du prélèvement). Sa hauteur suit son
         contenu en douceur : ouverture, fermeture, autre moyen choisi,
         formulaire de Stripe qui se charge (Karim 01/11 et 02/11/2026). -->
    <BoiteHauteurAnimee :id="idPanneau" :ouvert="panneau !== null" @referme="panneauReferme">
      <div
        v-if="panneauAffiche"
        class="mt-2 rounded-xl border border-gray-200 bg-gray-50 p-3 space-y-3"
        :data-testid="`panneau-${panneauAffiche}`"
        @keydown.esc="fermer"
      >
        <div class="flex items-center gap-3 text-sm">
          <span v-if="montant" class="font-medium text-gray-900">{{ t("don.moyensLocaux.donDe", { montant }) }}</span>
          <span v-else class="text-red-500">{{ t("don.moyensLocaux.choisirMontant") }}</span>
          <button type="button" class="ml-auto text-xs text-gray-600 underline hover:text-gray-900" @click="changerMontant">
            {{ t("don.moyensLocaux.changerMontant") }}
          </button>
          <button
            type="button"
            :aria-label="t('don.moyensLocaux.fermer')"
            :title="t('don.moyensLocaux.fermer')"
            :data-testid="`fermer-${panneauAffiche}`"
            class="-mr-1 size-7 shrink-0 rounded-full flex items-center justify-center text-gray-500 hover:bg-gray-200 hover:text-gray-900 transition-colors"
            @click="fermer"
          >
            <UIcon name="i-lucide-x" class="size-4" />
          </button>
        </div>
        <div v-if="champs.email">
          <label :for="`email-${panneauAffiche}`" class="block text-xs font-medium text-gray-700 mb-1">
            {{ t("don.moyensLocaux.email") }}
          </label>
          <input
            :id="`email-${panneauAffiche}`"
            v-model="emailPanneau"
            type="email"
            autocomplete="email"
            inputmode="email"
            class="w-full rounded-lg border bg-white px-3 py-2.5 text-sm focus:outline-none focus:border-gray-500"
            :class="messageEmail ? 'border-red-500 ring-1 ring-red-500' : 'border-gray-300'"
            :aria-invalid="messageEmail ? 'true' : undefined"
            @keydown.enter.prevent="payer(panneauAffiche)"
          >
          <p v-if="messageEmail" class="mt-1 text-xs text-red-600" role="alert">{{ messageEmail }}</p>
        </div>
        <div v-if="champs.nom">
          <label :for="`nom-titulaire-${panneauAffiche}`" class="block text-xs font-medium text-gray-700 mb-1">
            {{ t(cleTexteTitulaire("nomTitulaire", panneauAffiche), { moyen: nomDuMoyen(panneauAffiche) }) }}
          </label>
          <input
            :id="`nom-titulaire-${panneauAffiche}`"
            v-model="nomPanneau"
            type="text"
            :autocomplete="MOYENS_PAIEMENT_LOCAUX[panneauAffiche].titulaire === 'compte' ? 'name' : 'cc-name'"
            class="w-full rounded-lg border bg-white px-3 py-2.5 text-sm focus:outline-none focus:border-gray-500"
            :class="messageNom(panneauAffiche) ? 'border-red-500 ring-1 ring-red-500' : 'border-gray-300'"
            :aria-invalid="messageNom(panneauAffiche) ? 'true' : undefined"
            @keydown.enter.prevent="payer(panneauAffiche)"
          >
          <p v-if="anonyme" class="mt-1 text-[11px] text-gray-500" data-testid="note-anonyme">
            {{ t("don.moyensLocaux.anonymeNote", { moyen: nomDuMoyen(panneauAffiche) }) }}
          </p>
          <p v-if="messageNom(panneauAffiche)" class="mt-1 text-xs text-red-600" role="alert">{{ messageNom(panneauAffiche) }}</p>
        </div>
        <!-- Prélèvement SEPA : IBAN et texte du mandat, par Stripe. -->
        <template v-if="estPrelevement(panneauAffiche)">
          <div :id="idSepa" class="min-h-[96px]" data-testid="formulaire-sepa" />
          <p v-if="formulaireSepaIndisponible" class="text-xs text-red-600" role="alert">
            {{ t("don.moyensLocaux.prelevementIndisponible") }}
          </p>
        </template>
        <button
          type="button"
          :data-testid="`continuer-${panneauAffiche}`"
          :disabled="isLoading || (estPrelevement(panneauAffiche) && formulaireSepaIndisponible)"
          class="w-full h-11 rounded-lg text-white text-sm font-semibold transition-opacity hover:opacity-90 disabled:opacity-60 disabled:cursor-not-allowed"
          :style="{ backgroundColor: STYLE_MOYEN[panneauAffiche].texte }"
          @click="payer(panneauAffiche)"
        >
          {{
            estPrelevement(panneauAffiche)
              ? t("don.moyensLocaux.confirmerPrelevement")
              : t("don.moyensLocaux.continuer", { moyen: nomDuMoyen(panneauAffiche) })
          }}
        </button>
        <p class="text-[11px] text-gray-500 text-center" :data-testid="estPrelevement(panneauAffiche) ? 'note-prelevement' : undefined">
          {{
            estPrelevement(panneauAffiche)
              ? t(mensuel ? "don.moyensLocaux.notePrelevementMensuel" : "don.moyensLocaux.notePrelevement")
              : t("don.moyensLocaux.redirection", { moyen: nomDuMoyen(panneauAffiche) })
          }}
        </p>
      </div>
    </BoiteHauteurAnimee>

    <!-- Refus du paiement : une seule fois, sous le moyen choisi. -->
    <p v-if="erreur" class="mt-1.5 text-xs text-red-500 text-center" role="alert">{{ erreur }}</p>
  </div>
</template>
